import {
  initialize,
  LDClient,
  LDFlagSet,
  LDFlagValue,
  LDUser,
} from 'ldclient-js';

import { Storage } from '@trello/storage';
import { launchdarklyKey } from '@trello/config';
import { memberId } from '@trello/session-cookie';
import { Analytics } from '@trello/atlassian-analytics';

const USER_DATA_CACHE_TIMEOUT = 24 * 60 * 60 * 1000;
const USER_DATA_CACHE_STORAGE_KEY = 'featureFlagUserData';
const OVERRIDES_STORAGE_KEY = 'featureFlagOverrides';

interface UserData {
  channel: string;
  idEnterprises: string[];
  idOrgs: string[];
  orgs: string[];
}

interface UserDataCache {
  lastUpdated: number;
  key: string;
  data: UserData;
}

interface LDVariationMethod {
  (key: string, defaultValue?: LDFlagValue): LDFlagValue;
}

interface ChangeListener {
  (current?: LDFlagValue, previous?: LDFlagValue): void;
}
interface ClientListenerMap {
  [key: string]: ChangeListener;
}

interface ChangeListenerMap {
  [key: string]: ChangeListener[];
}

interface EventBindingMethod {
  (key: string, callback: ChangeListener): void;
}

interface FeatureFlagGroups {
  remote: LDFlagSet;
  overrides: LDFlagSet;
}

export type FlagSet = LDFlagSet;
export class FeatureFlagClient {
  private user: LDUser;

  private clientListeners: ClientListenerMap = {};
  private changeListeners: ChangeListenerMap = {};
  client: LDClient;

  constructor(user: LDUser) {
    this.user = user;

    const userData: UserDataCache = Storage.get(USER_DATA_CACHE_STORAGE_KEY);
    const isUserDataExpired =
      userData && Date.now() - userData.lastUpdated > USER_DATA_CACHE_TIMEOUT;
    const sameUser = userData && userData.key === this.user.key;

    if (userData) {
      if (!isUserDataExpired && sameUser) {
        this.user = {
          ...this.user,
          custom: {
            ...userData.data,
          },
        };
      } else {
        Storage.unset(USER_DATA_CACHE_STORAGE_KEY);
      }
    }

    this.client = initialize(launchdarklyKey, this.user, {
      bootstrap: 'localStorage',
      evaluationReasons: true,
      sendEventsOnlyForVariation: true,
      flushInterval: 60000,
    });
  }

  refineUserData(data: UserData) {
    const cacheData: UserDataCache = {
      lastUpdated: Date.now(),
      key: this.user.key || 'notLoggedIn',
      data,
    };

    Storage.set(USER_DATA_CACHE_STORAGE_KEY, cacheData);

    return this.client.identify({
      ...this.user,
      custom: {
        ...data,
      },
    });
  }

  get: LDVariationMethod = (key: string, defaultValue?: LDFlagValue) => {
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      return override;
    }

    return this.client.variation(key, defaultValue);
  };

  getTrackedVariation: LDVariationMethod = (
    key: string,
    defaultValue?: LDFlagValue,
  ) => {
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      FeatureFlagClient.triggerExposureEvent(key, override, 'OVERRIDE');
      return override;
    }

    const variationDetail = this.client.variationDetail(key, defaultValue);
    const { value, reason } = variationDetail;
    const { kind, ruleId } = reason;
    FeatureFlagClient.triggerExposureEvent(key, value, kind, ruleId);
    return value;
  };

  all(): FeatureFlagGroups {
    const remote = this.client.allFlags();
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};

    return {
      remote,
      overrides,
    };
  }

  private static triggerExposureEvent(
    flagKey: string,
    value: string,
    reason: string,
    ruleId?: string,
  ) {
    Analytics.sendTrackEvent({
      source: '@trello/feature-flag-client',
      actionSubject: 'feature',
      action: 'exposed',
      attributes: {
        flagKey,
        reason,
        ruleId,
        value,
      },
    });
  }

  private getClientListener(key: string): ChangeListener {
    if (Object.prototype.hasOwnProperty.call(this.clientListeners, key)) {
      return this.clientListeners[key];
    }

    this.clientListeners[key] = (current, previous) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        if (typeof this.getOverride(key) === 'undefined') {
          for (const listener of this.changeListeners[key]) {
            listener(current, previous);
          }
        } else {
          console.warn(
            `Ignoring feature flag change event for "${key}" from server due to local override`,
          );
        }
      }
    };

    return this.clientListeners[key];
  }

  on: EventBindingMethod = (key, callback) => {
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      this.changeListeners[key].push(callback);
    } else {
      this.changeListeners[key] = [callback];
      this.client.on(`change:${key}`, this.getClientListener(key));
    }
  };

  off: EventBindingMethod = (key, callback) => {
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      const idx = this.changeListeners[key].indexOf(callback);

      if (idx > -1) {
        this.changeListeners[key].splice(idx, 1);
      }

      if (this.changeListeners[key].length === 0) {
        this.client.off(`change:${key}`, this.getClientListener(key));
        delete this.clientListeners[key];
      }
    }
  };

  private getOverride(key: string): LDFlagValue {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY);

    if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key];
    }
  }

  setOverride(key: string, value: LDFlagValue): void {
    const originalValue = this.get(key);
    const existingOverrides: LDFlagSet =
      Storage.get(OVERRIDES_STORAGE_KEY) || {};

    Storage.set(OVERRIDES_STORAGE_KEY, {
      ...existingOverrides,
      [key]: value,
    });

    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      // Call all change listeners for this feature flag
      for (const listener of this.changeListeners[key]) {
        listener(value, originalValue);
      }
    }
  }

  resetOverrides(): void {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};
    Storage.set(OVERRIDES_STORAGE_KEY, {});

    // Call change listeners for all overrides that were just reset
    Object.entries(overrides).forEach(([key, originalValue]) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        for (const listener of this.changeListeners[key]) {
          listener(this.get(key), originalValue);
        }
      }
    });
  }
}

export const featureFlagClient = new FeatureFlagClient({
  key: memberId || 'notLoggedIn',
  anonymous: !memberId,
});
